import { BusinessObjectBase } from './base';
import { selectedStudentSchema } from '@schemas/business-objects/selected-student';
import type { NotAvailable } from './not-available';
import type { Student } from './student';
import type { StudentAssignment } from './student-assignment';
import type { Term } from './term';
import type { CalendarDate, Id } from '../base-types';
import type { Organization } from './organization';
import type { Slot } from './slot';
import type { TermTime } from './term-time';
import type { TermTutor } from './term-tutor';
import type { Tutor } from './tutor';
import type { User } from './user';

export interface SelectedStudentData {
  readonly id: Id;
  readonly created?: Date;
  readonly inactive?: boolean;
  readonly inactiveEffectiveDate?: CalendarDate;
  readonly statusNotes?: string;
  readonly termId: Id;
  readonly term?: Term;
  readonly studentId: Id;
  readonly student?: Student;
  readonly studentAssignments?: StudentAssignment[];
  readonly notAvailables?: NotAvailable[];
}

export interface SelectedStudent extends Readonly<SelectedStudentData> {}
type SelectedStudentCreateWithChildrenData = Partial<
  Omit<SelectedStudentData, 'studentAssignments'>
> & { studentAssignments?: Partial<StudentAssignment>[] };

export class SelectedStudent extends BusinessObjectBase {
  static readonly entityName = 'SelectedStudent' as const;
  static readonly plural = 'selected-students';
  static schema = selectedStudentSchema;

  // --- Static CRUD methods ---

  static async find(filter?: any): Promise<SelectedStudent[]> {
    const url = `${this.baseUrl}/${this.plural}`;
    const params: Record<string, string> = {};
    if (filter) params['filter'] = JSON.stringify(filter);
    const results = await this.get<any[]>(url, params);
    return results.map((data) => new SelectedStudent(data));
  }

  static async findOne(filter?: any): Promise<SelectedStudent | null> {
    const url = `${this.baseUrl}/${this.plural}/find-one`;
    const params: Record<string, string> = {};
    if (filter) params['filter'] = JSON.stringify(filter);
    const data = await this.get<any>(url, params);
    return data ? new SelectedStudent(data) : null;
  }

  static async findById(id: Id, filter?: any): Promise<SelectedStudent> {
    const url = `${this.baseUrl}/${this.plural}/${encodeURIComponent(String(id))}`;
    const params: Record<string, string> = {};
    if (filter) params['filter'] = JSON.stringify(filter);
    const data = await this.get<any>(url, params);
    return new SelectedStudent(data);
  }

  static async create(
    data: Omit<SelectedStudentData, 'id'>,
  ): Promise<SelectedStudent> {
    const url = `${this.baseUrl}/${this.plural}`;
    const result = await this.post<any>(url, data);
    return new SelectedStudent(result);
  }

  static async createMany(
    data: Omit<SelectedStudentData, 'id'>[],
  ): Promise<SelectedStudent[]> {
    const url = `${this.baseUrl}/${this.plural}/create-many`;
    const results = await this.post<any[]>(url, data);
    return results.map((item: any) => new SelectedStudent(item));
  }

  static async createWithChildren(
    data: SelectedStudentCreateWithChildrenData,
  ): Promise<SelectedStudent> {
    const url = `${this.baseUrl}/${this.plural}/create-with-children`;
    const result = await this.post<any>(url, data);
    return new SelectedStudent(result);
  }

  static async createManyWithChildren(
    data: SelectedStudentCreateWithChildrenData[],
  ): Promise<SelectedStudent[]> {
    const url = `${this.baseUrl}/${this.plural}/create-many-with-children`;
    const results = await this.post<any[]>(url, data);
    return results.map((item: any) => new SelectedStudent(item));
  }

  static async updateById(
    id: Id,
    updates: Partial<SelectedStudentData>,
  ): Promise<SelectedStudent> {
    const url = `${this.baseUrl}/${this.plural}/${encodeURIComponent(String(id))}`;
    const result = await this.patch<any>(url, updates);
    return new SelectedStudent(result);
  }

  static async deleteById(id: Id): Promise<boolean> {
    const url = `${this.baseUrl}/${this.plural}/${encodeURIComponent(String(id))}`;
    try {
      await this.del<void>(url);
      return true;
    } catch {
      return false;
    }
  }

  // --- Behaviors ---

  async markInactive(
    effectiveDate?: CalendarDate | null,
  ): Promise<SelectedStudent> {
    const url = `${BusinessObjectBase.baseUrl}/${SelectedStudent.plural}/${(this as any).id}/mark-inactive`;
    return BusinessObjectBase.post<SelectedStudent>(url, { effectiveDate });
  }
}
